import { useField, useFormikContext } from 'formik'
import { memo, ReactNode } from 'react'

import { ControlledTextInput as UIControlledTextInput } from '@consumer/ui'

type UIProps = Parameters<typeof UIControlledTextInput>[0]

/**
 * Old `IInputProps` surface accepted so call sites compile unchanged; drop
 * from call sites over time.
 *
 * Dead before this PR (never had an effect): `handleScrollYOffset`,
 * `useDebounce`, `debounceWait`, `value` (Formik always won).
 * Live before this PR, now standardized by the shared primitive (focus and
 * border styling are built in): `borderColor`, `_focus`, `bg`,
 * `containerBg`, `hintContainerBg`, `overflow`.
 */
interface LegacyPassthroughProps {
  /** @deprecated Formik state is the only source of the value. */
  value?: string
  borderColor?: string
  _focus?: unknown
  containerBg?: string
  hintContainerBg?: string
  bg?: string
  overflow?: string
  handleScrollYOffset?: number
  useDebounce?: boolean
  debounceWait?: number
}

export interface ControlledTextInputProps extends LegacyPassthroughProps {
  name: string
  label?: string
  hint?: string
  placeholder?: string
  optional?: boolean
  inputType?: 'text' | 'textarea'
  charLimit?: number
  charLimitCounter?: boolean
  errorMessageExternal?: string
  hideErrorMessage?: boolean
  isDisabled?: boolean
  autoComplete?: string
  /** Legacy lowercase spelling still used by some call sites. */
  autocomplete?: string
  autoFocus?: boolean
  keyboardType?: UIProps['keyboardType']
  leftIcon?: ReactNode
  leftHintIcon?: ReactNode
  width?: string | number
  style?: UIProps['style']
}

/**
 * CWA Formik binding over the shared `@consumer/ui` ControlledTextInput
 * (CBP-3943). Owns only the Formik wiring and CWA's `${name}-*` testID
 * contract (deliberately dropped: `\${name}-form-control` — the primitive
 * has no addressable container). Note `touched` is not wired historically;
 * `field.onBlur` is passed so touched-gated schemas work if ever adopted.
 */
const ControlledTextInput = ({
  name,
  label,
  hint,
  placeholder,
  optional,
  inputType = 'text',
  charLimit,
  charLimitCounter,
  errorMessageExternal,
  hideErrorMessage = false,
  isDisabled,
  autoComplete,
  autocomplete,
  autoFocus,
  keyboardType,
  leftIcon,
  leftHintIcon,
  width,
  style,
}: ControlledTextInputProps) => {
  const [field, meta, helpers] = useField<string>(name)
  const { isSubmitting } = useFormikContext()

  const isInvalid = Boolean(meta.error?.length || errorMessageExternal)
  const errorMessage = !hideErrorMessage && meta.error && !errorMessageExternal ? meta.error : undefined

  return (
    <UIControlledTextInput
      value={field.value ?? ''}
      onChangeText={text => helpers.setValue(text)}
      onBlur={field.onBlur(name)}
      label={label}
      optionalLabel={optional ? 'OPTIONAL' : undefined}
      placeholder={placeholder}
      hintText={hint}
      hintSlot={leftHintIcon}
      errorMessage={errorMessage}
      isInvalid={isInvalid}
      isDisabled={isDisabled || isSubmitting}
      maxLength={charLimit}
      showCharCount={Boolean(charLimitCounter && charLimit)}
      leftSlot={leftIcon}
      autoFocus={autoFocus}
      // legacy IInputProps surface: arbitrary strings reach here unvalidated
      autoComplete={(autoComplete ?? autocomplete) as UIProps['autoComplete']}
      keyboardType={keyboardType}
      testID={inputType === 'textarea' ? `${name}-textarea-input` : `${name}-text-input`}
      labelTestID={`${name}-label`}
      optionalLabelTestID={`${name}-optional-label`}
      hintTestID={`${name}-hint`}
      errorTestID={`${name}-error`}
      charCountTestID={`${name}-char-limit-label`}
      style={[width !== undefined ? { width } : null, style] as UIProps['style']}
      {...(inputType === 'textarea' ? { multiline: true as const } : {})}
    />
  )
}

export default memo(ControlledTextInput)
